import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { RotateCcw, Trash2, Phone, ArrowRight, Pencil, X, FastForward, History } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";
import PromptDialog from "../../components/PromptDialog";

const STATUS_LABEL = {
  waiting: { label: "En attente", cls: "bg-[#FFFBE6] text-[#9A7D00]" },
  called: { label: "Appelé", cls: "bg-brand/10 text-brand" },
  present: { label: "Terminé", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
  absent: { label: "Annulé", cls: "bg-surface text-subtle" },
};

export default function AdminQueues() {
  const [queues, setQueues] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState({ status: "", commerceId: "", dateFrom: "", dateTo: "" });
  const [commerces, setCommerces] = useState([]);
  const [editTicket, setEditTicket] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteQueueTarget, setDeleteQueueTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTicketTarget, setDeleteTicketTarget] = useState(null);
  const [historyTicket, setHistoryTicket] = useState(null);

  const load = async () => {
    try {
      const [qs, tks, cs] = await Promise.all([
        api.get("/admin/queues"),
        api.get("/admin/tickets", {
          params: {
            limit: 200,
            status: filter.status || undefined,
            commerceId: filter.commerceId || undefined,
            dateFrom: filter.dateFrom ? new Date(filter.dateFrom + "T00:00:00").toISOString() : undefined,
            dateTo: filter.dateTo ? new Date(filter.dateTo + "T23:59:59").toISOString() : undefined,
          },
        }),
        api.get("/admin/commerces-list"),
      ]);
      setQueues(qs.data);
      setTickets(tks.data.items || []);
      setCommerces(cs.data || []);
    } catch { setQueues([]); }
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [filter.status, filter.commerceId, filter.dateFrom, filter.dateTo]);

  const resetQueue = (q) => setResetTarget(q);

  const performResetQueue = async () => {
    const q = resetTarget;
    setResetTarget(null);
    if (!q) return;
    try { await api.post(`/admin/queues/${q.id}/reset`); toast.success("File réinitialisée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleOpen = async (q) => {
    try { await api.post(`/admin/queues/${q.id}/toggle-open`); toast.success("État file modifié"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteQueue = (q) => setDeleteQueueTarget(q);

  const performDeleteQueue = async () => {
    const q = deleteQueueTarget;
    setDeleteQueueTarget(null);
    if (!q) return;
    try { await api.delete(`/admin/queues/${q.id}`); toast.success("File supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const renameQueue = (q) => setRenameTarget(q);

  // Force-expire a PAID additional queue : sets `additionalExpiresAt` 2min
  // in the past so the lifecycle loop's next tick drains/closes it. Useful
  // for end-to-end testing of the 24h expiry + drain + post-closure email
  // flow without waiting 23h59. Admin-only. Spec fork 19+.
  const forceExpireQueue = async (q) => {
    if (!window.confirm(
      `Forcer l'expiration des 24h de la file « ${q.name} » de l'établissement ${q.commerceName} ?\n\nLa file passera en fermeture programmée à la prochaine itération du lifecycle loop (≤ 60s).\n\nSi elle a encore des tickets actifs, elle restera ouverte pour les écouler avant fermeture définitive et envoi de l'email post-fermeture.`
    )) return;
    try {
      await api.post(`/admin/queues/${q.id}/force-expire`);
      toast.success(`File « ${q.name} » : expiration forcée — drain dans ≤ 60s`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const performRenameQueue = async (newName) => {
    const q = renameTarget;
    setRenameTarget(null);
    if (!q) return;
    const name = (newName || "").trim();
    if (!name || name === q.name) return;
    try { await api.patch(`/admin/queues/${q.id}`, { name }); toast.success("Renommée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const callTicket = async (t) => {
    try { await api.post(`/admin/tickets/${t.id}/call`); toast.success(`Ticket n°${t.number} appelé`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteTicket = (t) => setDeleteTicketTarget(t);

  const performDeleteTicket = async () => {
    const t = deleteTicketTarget;
    setDeleteTicketTarget(null);
    if (!t) return;
    try { await api.delete(`/admin/tickets/${t.id}`); toast.success("Ticket supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setStatus = async (t, status) => {
    try { await api.patch(`/admin/tickets/${t.id}`, { status }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!queues) return <div className="text-subtle">Chargement…</div>;

  return (
    <div data-testid="admin-queues-page">
      <h2 className="font-display text-2xl font-black text-ink mb-4">Files & Tickets</h2>

      <section className="mb-8">
        <p className="overline mb-3">Files d'attente ({queues.length})</p>
        <div className="border border-border overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-surface/60 text-subtle text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">File</th>
                <th className="text-left p-3">Établissement</th>
                <th className="text-left p-3">Active</th>
                <th className="text-left p-3">En attente</th>
                <th className="text-left p-3">Appelé</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {queues.map((q) => (
                <tr key={q.id} className="border-t border-border" data-testid={`admin-queue-row-${q.id}`}>
                  <td className="p-3 text-ink">
                    {q.name} {q.isDefault && <span className="ml-1 text-[10px] text-subtle">(principale)</span>}
                    {q.isAdditional && (
                      <span className="ml-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-[#F18F18] text-white rounded uppercase tracking-wider" title="File supplémentaire payante 4,90 € TTC">
                        Suppl. 4,90€
                      </span>
                    )}
                    {q.drainMode && (
                      <span className="ml-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-[#D7263D] text-white rounded uppercase tracking-wider" title={q.drainReason}>
                        Drain ({q.drainReason || "?"})
                      </span>
                    )}
                    {q.additionalExpiresAt && !q.drainMode && (
                      <span className="ml-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-zinc-200 text-zinc-700 rounded uppercase tracking-wider font-mono" title={`Expire le ${new Date(q.additionalExpiresAt).toLocaleString("fr-FR")}`}>
                        ⏱ {new Date(q.additionalExpiresAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-subtle text-xs">{q.commerceName}</td>
                  <td className="p-3">{q.isActive ? "Oui" : "Non"}</td>
                  <td className="p-3 font-mono">{q.waitingCount}</td>
                  <td className="p-3 font-mono">{q.calledCount}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <IconBtn onClick={() => toggleOpen(q)} title="Ouvrir/Fermer étab." icon={<Phone size={12} />} testid={`queue-toggle-open-${q.id}`} />
                      <IconBtn onClick={() => renameQueue(q)} title="Renommer" icon={<Pencil size={12} />} testid={`queue-rename-${q.id}`} />
                      <IconBtn onClick={() => resetQueue(q)} title="Réinitialiser" icon={<RotateCcw size={12} />} testid={`queue-reset-${q.id}`} />
                      {q.isAdditional && !q.drainMode && (
                        <IconBtn
                          onClick={() => forceExpireQueue(q)}
                          title="Forcer expiration 24 h (test)"
                          icon={<FastForward size={12} />}
                          testid={`queue-force-expire-${q.id}`}
                          variant="warn"
                        />
                      )}
                      {!q.isDefault && <IconBtn onClick={() => deleteQueue(q)} title="Supprimer" icon={<Trash2 size={12} />} testid={`queue-delete-${q.id}`} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* MOBILE — cards */}
        <div className="md:hidden space-y-2" data-testid="admin-queues-cards-mobile">
          {queues.map((q) => (
            <div key={q.id} className="border border-border bg-white p-3" data-testid={`admin-queue-card-${q.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink truncate">
                    {q.name} {q.isDefault && <span className="ml-1 text-[10px] text-subtle">(principale)</span>}
                  </p>
                  <p className="text-xs text-subtle truncate">{q.commerceName}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${q.isActive ? "bg-[#ECFBEF] text-[#0A7F2E]" : "bg-zinc-200 text-zinc-600"}`}>
                  {q.isActive ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="flex gap-1 flex-wrap mb-2">
                {q.isAdditional && <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-[#F18F18] text-white rounded uppercase tracking-wider">Suppl. 4,90€</span>}
                {q.drainMode && <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-[#D7263D] text-white rounded uppercase tracking-wider">Drain ({q.drainReason || "?"})</span>}
                {q.additionalExpiresAt && !q.drainMode && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold bg-zinc-200 text-zinc-700 rounded uppercase tracking-wider font-mono">
                    ⏱ {new Date(q.additionalExpiresAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                <div className="text-[11px] text-subtle">
                  <span className="font-mono font-bold text-ink">{q.waitingCount}</span> en attente · <span className="font-mono font-bold text-ink">{q.calledCount}</span> appelé
                </div>
                <div className="inline-flex gap-1 flex-wrap justify-end">
                  <IconBtn onClick={() => toggleOpen(q)} title="Ouvrir/Fermer étab." icon={<Phone size={12} />} testid={`queue-toggle-open-m-${q.id}`} />
                  <IconBtn onClick={() => renameQueue(q)} title="Renommer" icon={<Pencil size={12} />} testid={`queue-rename-m-${q.id}`} />
                  <IconBtn onClick={() => resetQueue(q)} title="Réinitialiser" icon={<RotateCcw size={12} />} testid={`queue-reset-m-${q.id}`} />
                  {q.isAdditional && !q.drainMode && (
                    <IconBtn onClick={() => forceExpireQueue(q)} title="Forcer expiration 24 h" icon={<FastForward size={12} />} testid={`queue-force-expire-m-${q.id}`} variant="warn" />
                  )}
                  {!q.isDefault && <IconBtn onClick={() => deleteQueue(q)} title="Supprimer" icon={<Trash2 size={12} />} testid={`queue-delete-m-${q.id}`} />}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="overline">Tickets ({tickets.length})</p>
          <div className="flex gap-2 flex-wrap items-center">
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="h-9 border border-border px-2 text-xs" data-testid="admin-tickets-filter-status">
              <option value="">Tous statuts</option>
              <option value="waiting">En attente</option>
              <option value="called">Appelé</option>
              <option value="present">Terminé</option>
              <option value="absent">Annulé</option>
            </select>
            <select value={filter.commerceId} onChange={(e) => setFilter({ ...filter, commerceId: e.target.value })} className="h-9 border border-border px-2 text-xs" data-testid="admin-tickets-filter-commerce">
              <option value="">Tous établissements</option>
              {commerces.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="inline-flex items-center gap-1 text-[10px] text-subtle uppercase tracking-wider">
              <span>Du</span>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
                className="h-9 border border-border px-2 text-xs"
                data-testid="admin-tickets-filter-date-from"
              />
            </label>
            <label className="inline-flex items-center gap-1 text-[10px] text-subtle uppercase tracking-wider">
              <span>Au</span>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
                className="h-9 border border-border px-2 text-xs"
                data-testid="admin-tickets-filter-date-to"
              />
            </label>
            {(filter.status || filter.commerceId || filter.dateFrom || filter.dateTo) && (
              <button
                type="button"
                onClick={() => setFilter({ status: "", commerceId: "", dateFrom: "", dateTo: "" })}
                className="h-9 border border-border bg-white px-2 text-xs font-bold uppercase tracking-wider text-subtle hover:border-ink hover:text-ink"
                data-testid="admin-tickets-filter-reset"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>
        <div className="border border-border overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-surface/60 text-subtle text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">N°</th>
                <th className="text-left p-3">Visiteur</th>
                <th className="text-left p-3">Établissement / File</th>
                <th className="text-left p-3">Créé</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-subtle">Aucun ticket</td></tr>}
              {tickets.map((t) => {
                const s = STATUS_LABEL[t.status] || STATUS_LABEL.waiting;
                return (
                  <tr key={t.id} className="border-t border-border" data-testid={`admin-ticket-row-${t.id}`}>
                    <td className="p-3 font-mono font-bold text-ink">#{t.number}</td>
                    <td className="p-3 text-xs">
                      {t.userName}<br />
                      <span className="text-subtle">{t.userEmail}</span>
                    </td>
                    <td className="p-3 text-xs">
                      {t.commerceName}{t.queueName && t.queueName !== "File principale" ? <span className="ml-1 text-[10px] font-bold bg-ink text-white px-1">{t.queueName}</span> : null}
                    </td>
                    <td className="p-3 text-subtle text-xs">{new Date(t.createdAt).toLocaleString("fr-FR")}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <IconBtn onClick={() => setHistoryTicket(t)} title="Voir évolution" icon={<History size={12} />} testid={`ticket-history-${t.id}`} />
                        {t.status === "waiting" && <IconBtn onClick={() => callTicket(t)} title="Appeler" icon={<Phone size={12} />} testid={`ticket-call-${t.id}`} />}
                        {t.status === "called" && <IconBtn onClick={() => setStatus(t, "present")} title="Terminé" icon={<ArrowRight size={12} />} testid={`ticket-present-${t.id}`} />}
                        {t.status !== "absent" && <IconBtn onClick={() => setStatus(t, "absent")} title="Annuler" icon={<X size={12} />} testid={`ticket-absent-${t.id}`} />}
                        <IconBtn onClick={() => setEditTicket(t)} title="Éditer / déplacer" icon={<Pencil size={12} />} testid={`ticket-edit-${t.id}`} />
                        <IconBtn onClick={() => deleteTicket(t)} title="Supprimer" icon={<Trash2 size={12} />} testid={`ticket-delete-${t.id}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MOBILE — tickets en cards */}
        <div className="md:hidden space-y-2" data-testid="admin-tickets-cards-mobile">
          {tickets.length === 0 && <div className="border border-border p-6 text-center text-subtle text-sm">Aucun ticket</div>}
          {tickets.map((t) => {
            const s = STATUS_LABEL[t.status] || STATUS_LABEL.waiting;
            return (
              <div key={t.id} className="border border-border bg-white p-3" data-testid={`admin-ticket-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-mono font-black text-lg text-ink">#{t.number}</p>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${s.cls}`}>{s.label}</span>
                </div>
                <p className="text-xs text-ink truncate">{t.userName || "—"}</p>
                <p className="text-[11px] text-subtle truncate">{t.userEmail || "—"}</p>
                <p className="text-[11px] text-subtle mt-1">
                  {t.commerceName}
                  {t.queueName && t.queueName !== "File principale" && (
                    <span className="ml-1 text-[10px] font-bold bg-ink text-white px-1">{t.queueName}</span>
                  )}
                </p>
                <p className="text-[10px] text-subtle mt-0.5">{new Date(t.createdAt).toLocaleString("fr-FR")}</p>
                <div className="inline-flex gap-1 flex-wrap mt-2 pt-2 border-t border-border w-full">
                  <IconBtn onClick={() => setHistoryTicket(t)} title="Voir évolution" icon={<History size={12} />} testid={`ticket-history-m-${t.id}`} />
                  {t.status === "waiting" && <IconBtn onClick={() => callTicket(t)} title="Appeler" icon={<Phone size={12} />} testid={`ticket-call-m-${t.id}`} />}
                  {t.status === "called" && <IconBtn onClick={() => setStatus(t, "present")} title="Terminé" icon={<ArrowRight size={12} />} testid={`ticket-present-m-${t.id}`} />}
                  {t.status !== "absent" && <IconBtn onClick={() => setStatus(t, "absent")} title="Annuler" icon={<X size={12} />} testid={`ticket-absent-m-${t.id}`} />}
                  <IconBtn onClick={() => setEditTicket(t)} title="Éditer / déplacer" icon={<Pencil size={12} />} testid={`ticket-edit-m-${t.id}`} />
                  <IconBtn onClick={() => deleteTicket(t)} title="Supprimer" icon={<Trash2 size={12} />} testid={`ticket-delete-m-${t.id}`} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {editTicket && <EditTicketModal ticket={editTicket} queues={queues.filter((q) => q.commerceId === editTicket.commerceId)} onClose={() => setEditTicket(null)} onDone={() => { setEditTicket(null); load(); }} />}

      <ConfirmDialog
        open={!!resetTarget}
        title={resetTarget ? `Réinitialiser « ${resetTarget.name} » ?` : "Réinitialiser"}
        message={
          <>
            <p>Tous les tickets actifs seront <strong>annulés</strong>.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        }
        confirmLabel="OK"
        destructive
        onConfirm={performResetQueue}
        onCancel={() => setResetTarget(null)}
        testid="admin-queue-reset-confirm"
      />
      <ConfirmDialog
        open={!!deleteQueueTarget}
        title={deleteQueueTarget ? `Supprimer « ${deleteQueueTarget.name} » ?` : "Supprimer"}
        message={<p className="text-destructive font-bold">Cette action est irréversible.</p>}
        confirmLabel="Supprimer"
        destructive
        onConfirm={performDeleteQueue}
        onCancel={() => setDeleteQueueTarget(null)}
        testid="admin-queue-delete-confirm"
      />
      <PromptDialog
        open={!!renameTarget}
        title="Renommer la file"
        message="Saisissez le nouveau nom de la file."
        defaultValue={renameTarget?.name || ""}
        maxLength={40}
        confirmLabel="Valider"
        onConfirm={performRenameQueue}
        onCancel={() => setRenameTarget(null)}
        testid="admin-queue-rename"
      />
      <ConfirmDialog
        open={!!deleteTicketTarget}
        title={deleteTicketTarget ? `Supprimer le ticket n°${deleteTicketTarget.number} ?` : "Supprimer"}
        message={<p className="text-destructive font-bold">Cette action est irréversible.</p>}
        confirmLabel="Supprimer"
        destructive
        onConfirm={performDeleteTicket}
        onCancel={() => setDeleteTicketTarget(null)}
        testid="admin-ticket-delete-confirm"
      />
      {historyTicket && (
        <TicketHistoryModal ticket={historyTicket} onClose={() => setHistoryTicket(null)} />
      )}
    </div>
  );
}

function IconBtn({ onClick, title, icon, testid, variant }) {
  const cls = variant === "warn"
    ? "h-7 w-7 border border-[#F18F18] bg-[#F18F18] text-white inline-flex items-center justify-center hover:bg-[#D77A0F]"
    : "h-7 w-7 border border-border inline-flex items-center justify-center hover:border-ink bg-white";
  return <button onClick={onClick} title={title} data-testid={testid} className={cls}>{icon}</button>;
}

function TicketHistoryModal({ ticket, onClose }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/tickets/${ticket.id}/history`);
        if (mounted) setEvents(data?.items || []);
      } catch (err) {
        if (mounted) {
          setEvents([]);
          toast.error(err?.response?.data?.detail || "Erreur de chargement");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [ticket.id]);

  const downloadOne = async (eventId) => {
    try {
      const { data } = await api.get(`/admin/history/events/${eventId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `evenement-ticket-${ticket.number}-${eventId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="ticket-history-modal-backdrop"
    >
      <div
        className="bg-white border-2 border-brand max-w-2xl w-full rounded-xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="ticket-history-modal"
      >
        <div className="px-5 py-4 border-b border-border bg-surface/60 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="overline text-[10px] text-brand mb-0.5">Évolution du ticket</p>
            <h3 className="font-display text-lg font-black text-ink leading-tight">
              Ticket #{ticket.number} · {ticket.commerceName}
              {ticket.queueName && ticket.queueName !== "File principale" ? (
                <span className="ml-2 text-[10px] font-bold bg-ink text-white px-1.5 py-0.5">{ticket.queueName}</span>
              ) : null}
            </h3>
            <p className="text-xs text-subtle mt-0.5">
              Visiteur : {ticket.userName || "—"} · Créé le {new Date(ticket.createdAt).toLocaleString("fr-FR")}
            </p>
          </div>
          <button onClick={onClose} className="text-subtle hover:text-ink p-1" aria-label="Fermer" data-testid="ticket-history-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-white">
          {loading && !events && <p className="text-sm text-subtle italic">Chargement…</p>}
          {events && events.length === 0 && (
            <p className="text-sm text-subtle italic" data-testid="ticket-history-empty">
              Aucune transition d'état enregistrée pour ce ticket.
            </p>
          )}
          {events && events.length > 0 && (
            <ol className="space-y-3" data-testid="ticket-history-timeline">
              {events.map((e) => {
                const dt = e.at ? new Date(e.at) : null;
                const dtLabel = dt ? dt.toLocaleString("fr-FR", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                }) : "—";
                return (
                  <li
                    key={e.id}
                    className="flex gap-3 border-l-2 pl-3"
                    style={{ borderColor: e.color || "#888" }}
                    data-testid={`ticket-history-event-${e.type}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold leading-tight" style={{ color: e.color || "#111" }}>{e.label}</p>
                      <p className="text-xs text-subtle mt-0.5">{dtLabel}</p>
                      {e.details && <p className="text-xs text-ink mt-1 leading-snug">{e.details}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadOne(e.id)}
                      className="self-start shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border hover:border-brand text-[10px] font-bold uppercase tracking-wider text-subtle hover:text-ink"
                      data-testid={`ticket-history-event-download-${e.id}`}
                    >
                      PDF
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function EditTicketModal({ ticket, queues, onClose, onDone }) {
  const [number, setNumber] = useState(ticket.number);
  const [queueId, setQueueId] = useState(ticket.queueId || "");
  const [status, setStatusLocal] = useState(ticket.status);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = {};
      if (Number(number) !== ticket.number) body.number = Number(number);
      if (queueId && queueId !== ticket.queueId) body.queueId = queueId;
      if (status !== ticket.status) body.status = status;
      if (Object.keys(body).length) await api.patch(`/admin/tickets/${ticket.id}`, body);
      toast.success("Ticket mis à jour");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-md w-full mt-20" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display text-lg font-bold text-ink">Éditer ticket n°{ticket.number}</h3>
          <button onClick={onClose} className="text-subtle hover:text-ink"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-subtle uppercase tracking-wider font-bold">Numéro</label>
            <input type="number" min={1} value={number} onChange={(e) => setNumber(e.target.value)} className="mt-1 w-full h-10 border border-border px-3" data-testid="edit-ticket-number" />
          </div>
          <div>
            <label className="text-xs text-subtle uppercase tracking-wider font-bold">File</label>
            <select value={queueId} onChange={(e) => setQueueId(e.target.value)} className="mt-1 w-full h-10 border border-border px-3" data-testid="edit-ticket-queue">
              {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-subtle uppercase tracking-wider font-bold">Statut</label>
            <select value={status} onChange={(e) => setStatusLocal(e.target.value)} className="mt-1 w-full h-10 border border-border px-3" data-testid="edit-ticket-status">
              <option value="waiting">En attente</option>
              <option value="called">Appelé</option>
              <option value="present">Terminé</option>
              <option value="absent">Annulé</option>
            </select>
          </div>
          <Button onClick={submit} disabled={saving} className="w-full h-11 bg-brand text-white rounded-md" data-testid="edit-ticket-save">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}
